import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check, Save, Globe, MapPin } from 'lucide-react';
import api from '../utils/api';
import { isAuthenticated } from '../utils/auth';

const CITIES = [
  'Bengaluru', 'Mumbai', 'Delhi', 'Hyderabad', 'Chennai',
  'Pune', 'Kolkata', 'Ahmedabad', 'Jaipur', 'Gurgaon',
  'Noida', 'Chandigarh', 'Indore', 'Lucknow', 'Kochi',
  'Coimbatore', 'Nagpur', 'Surat', 'Bhopal', 'Other'
];

const COUNTRIES = [
  'India', 'United States', 'United Kingdom', 'Canada', 'Australia',
  'Germany', 'France', 'UAE', 'Singapore', 'Other'
];

const GENDERS = ['Man', 'Woman', 'Non-binary'];
const GENDER_PREFS = ['Men', 'Women', 'Everyone'];

const RELATIONSHIP_TYPES = [
  { id: 'long-term', label: 'Long-term relationship', emoji: '💍' },
  { id: 'casual', label: 'Casual dating', emoji: '🌸' },
  { id: 'friendship', label: 'New friends', emoji: '👋' },
  { id: 'hookup', label: 'Hookup', emoji: '🔥' },
  { id: 'not-sure', label: 'Not sure yet', emoji: '🤷' },
  { id: 'marriage', label: 'Marriage', emoji: '💒' }
];

const INTERESTS = [
  'Coding/Tech', 'Movies/Series', 'Travel', 'Music', 'Fitness',
  'Books', 'Gaming', 'Cooking', 'Art', 'Sports',
  'Startups', 'Photography', 'Dance', 'Fashion', 'Food',
  'Hiking', 'Yoga', 'Meditation', 'Writing', 'Podcasts',
  'Anime', 'Cricket', 'Football', 'Pets', 'Volunteering',
  'Nightlife', 'Wine/Beer', 'Coffee', 'Netflix', 'Music Festivals',
  'Board Games', 'Running', 'Swimming', 'Cycling', 'Camping'
];

const INCOME_RANGES = [
  'Prefer not to say',
  'Below 5 LPA',
  '5-10 LPA',
  '10-20 LPA',
  '20-50 LPA',
  '50+ LPA'
];

const HEIGHT_OPTIONS = [
  "Below 5'0\" (152cm)",
  "5'0\" - 5'3\" (152-160cm)",
  "5'4\" - 5'6\" (163-168cm)",
  "5'7\" - 5'9\" (170-175cm)",
  "5'10\" - 6'0\" (178-183cm)",
  "6'1\" - 6'3\" (185-190cm)",
  "Above 6'3\" (191cm+)"
];

const GYM_OPTIONS = [
  'Regular gym-goer',
  'Occasionally active',
  'Prefer outdoor activities',
  'Not into fitness'
];

const LOCATION_PREFERENCES = [
  { id: 'same-city', label: 'Same city only', icon: MapPin },
  { id: 'same-country', label: 'Anywhere in my country', icon: MapPin },
  { id: 'worldwide', label: 'Worldwide', icon: Globe }
];

const MUSIC_GENRES = [
  'Bollywood', 'Pop', 'Rock', 'Hip-Hop', 'EDM', 'Classical',
  'Jazz', 'R&B', 'Indie', 'Metal', 'Country', 'Sufi', 'Devotional'
];

const IDEOLOGIES = [
  'Liberal', 'Conservative', 'Moderate', 'Apolitical', 'Progressive'
];

const GREEN_FLAGS = [
  'Good listener', 'Emotionally available', 'Ambitious', 'Funny',
  'Family-oriented', 'Adventurous', 'Loyal', 'Honest', 'Supportive'
];

const RED_FLAGS = [
  'Ghosting', 'Jealousy', 'Dishonesty', 'Poor communication',
  'No ambition', 'Disrespectful', 'Controlling', 'Negativity'
];

export default function EditProfile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    city: '',
    country: 'India',
    min_age: 18,
    max_age: 35,
    gender: '',
    gender_pref: '',
    interests: [],
    primary_interests: [],
    relationship_type: '',
    bio: '',
    location_preference: 'same-city',
    music_preferences: [],
    ideology: '',
    green_flags: [],
    red_flags: [],
    hobbies: '',
    income_range: '',
    height: '',
    gym: ''
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    const loadProfile = async () => {
      try {
        const res = await api.get('/user/profile');
        setFormData({
          city: res.data.city || '',
          country: res.data.country || 'India',
          min_age: res.data.min_age || 18,
          max_age: res.data.max_age || 35,
          gender: res.data.gender || '',
          gender_pref: res.data.gender_pref || '',
          interests: res.data.interests || [],
          primary_interests: res.data.primary_interests || [],
          relationship_type: res.data.relationship_type || '',
          bio: res.data.bio || '',
          location_preference: res.data.location_preference || 'same-city',
          music_preferences: res.data.music_preferences || [],
          ideology: res.data.ideology || '',
          green_flags: res.data.green_flags || [],
          red_flags: res.data.red_flags || [],
          hobbies: res.data.hobbies || '',
          income_range: res.data.income_range || '',
          height: res.data.height || '',
          gym: res.data.gym || ''
        });
      } catch (error) {
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [navigate]);

  const toggleInterest = (interest) => {
    if (formData.interests.includes(interest)) {
      setFormData({
        ...formData,
        interests: formData.interests.filter(i => i !== interest)
      });
    } else if (formData.interests.length < 10) {
      setFormData({
        ...formData,
        interests: [...formData.interests, interest]
      });
    }
  };

  const toggleArrayItem = (field, item, maxItems = 5) => {
    const current = formData[field];
    if (current.includes(item)) {
      setFormData({ ...formData, [field]: current.filter(i => i !== item) });
    } else if (current.length < maxItems) {
      setFormData({ ...formData, [field]: [...current, item] });
    }
  };

  const togglePrimaryInterest = (interest) => {
    const current = formData.primary_interests;
    if (current.includes(interest)) {
      setFormData({ ...formData, primary_interests: current.filter(i => i !== interest) });
    } else if (current.length < 3) {
      setFormData({ ...formData, primary_interests: [...current, interest] });
    } else {
      toast.error('Maximum 3 primary interests allowed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.city || !formData.gender || !formData.gender_pref) {
      toast.error('All fields are required');
      return;
    }
    
    if (formData.min_age > formData.max_age) {
      toast.error('Minimum age cannot be greater than maximum age');
      return;
    }
    
    if (formData.interests.length < 3) {
      toast.error('Select at least 3 interests');
      return;
    }
    
    if (formData.primary_interests.length < 1) {
      toast.error('Select at least 1 primary interest');
      return;
    }
    
    setSaving(true);
    try {
      await api.put('/user/profile', formData);
      toast.success('Profile updated! Your preferences will apply to future matches.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container gradient-bg min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-6 pb-20 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/dashboard')}
            className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center hover:bg-secondary/70 transition-colors"
          >
            <ArrowLeft className="text-white" size={20} />
          </button>
          <h1 className="text-2xl font-bold text-white">Edit Profile</h1>
        </div>

        <div className="glass rounded-2xl p-4 mb-6 border border-amber-500/30">
          <p className="text-amber-400 text-sm">
            💡 Updating preferences helps find better matches. Changes apply to future matches.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Bio */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Bio</label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData({...formData, bio: e.target.value.slice(0, 300)})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none h-24"
              placeholder="Tell people about yourself..."
              maxLength={300}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">{formData.bio.length}/300</p>
          </div>

          {/* Location */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">Country</label>
              <select
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">City</label>
              <select
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">Select city</option>
                {CITIES.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Location Preference */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Match Location</label>
            <div className="grid grid-cols-3 gap-2">
              {LOCATION_PREFERENCES.map(pref => (
                <button
                  key={pref.id}
                  type="button"
                  onClick={() => setFormData({...formData, location_preference: pref.id})}
                  className={`p-3 rounded-xl text-center transition-all ${
                    formData.location_preference === pref.id 
                      ? 'bg-primary text-white' 
                      : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  <pref.icon className="mx-auto mb-1" size={18} />
                  <p className="text-xs">{pref.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Age Range */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">
              Age Range: {formData.min_age} - {formData.max_age}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="number"
                min="18"
                max="100"
                value={formData.min_age}
                onChange={(e) => setFormData({...formData, min_age: Math.max(18, parseInt(e.target.value) || 18)})}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
              <input
                type="number"
                min="18"
                max="100"
                value={formData.max_age}
                onChange={(e) => setFormData({...formData, max_age: Math.min(100, parseInt(e.target.value) || 35)})}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              />
            </div>
          </div>

          {/* Gender */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">I am</label>
              <select
                value={formData.gender}
                onChange={(e) => setFormData({...formData, gender: e.target.value})}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">Select</option>
                {GENDERS.map(gender => (
                  <option key={gender} value={gender}>{gender}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-400 mb-1.5 block">Looking for</label>
              <select
                value={formData.gender_pref}
                onChange={(e) => setFormData({...formData, gender_pref: e.target.value})}
                className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              >
                <option value="">Select</option>
                {GENDER_PREFS.map(pref => (
                  <option key={pref} value={pref}>{pref}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Relationship Type */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Looking for</label>
            <div className="grid grid-cols-3 gap-2">
              {RELATIONSHIP_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({...formData, relationship_type: type.id})}
                  className={`p-3 rounded-xl text-center transition-all ${
                    formData.relationship_type === type.id 
                      ? 'bg-primary text-white' 
                      : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  <span className="text-xl block mb-1">{type.emoji}</span>
                  <p className="text-xs">{type.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Primary Interests */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">
              Primary Interests ({formData.primary_interests.length}/3) <span className="text-primary">*Most important</span>
            </label>
            <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
              {INTERESTS.map(interest => {
                const isSelected = formData.primary_interests.includes(interest);
                const isDisabled = !isSelected && formData.primary_interests.length >= 3;
                
                return (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => !isDisabled && togglePrimaryInterest(interest)}
                    disabled={isDisabled}
                    className={`h-10 rounded-lg font-medium text-xs transition-all flex items-center justify-center gap-1
                      ${isSelected 
                        ? 'bg-amber-500 text-white ring-2 ring-amber-400' 
                        : isDisabled
                          ? 'bg-secondary/30 text-gray-500 cursor-not-allowed'
                          : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                  >
                    {isSelected && <Check size={12} />}
                    {interest}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">At least 1 must match with potential partners</p>
          </div>

          {/* Other Interests */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">
              Other Interests ({formData.interests.length}/10)
            </label>
            <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-y-auto">
              {INTERESTS.filter(i => !formData.primary_interests.includes(i)).map(interest => {
                const isSelected = formData.interests.includes(interest);
                const isDisabled = !isSelected && formData.interests.length >= 10;
                
                return (
                  <button
                    key={interest}
                    type="button"
                    onClick={() => !isDisabled && toggleInterest(interest)}
                    disabled={isDisabled}
                    className={`h-10 rounded-lg font-medium text-xs transition-all flex items-center justify-center gap-1
                      ${isSelected 
                        ? 'bg-primary text-white' 
                        : isDisabled
                          ? 'bg-secondary/30 text-gray-500 cursor-not-allowed'
                          : 'bg-secondary/50 text-white border border-white/10'
                      }`}
                  >
                    {isSelected && <Check size={12} />}
                    {interest}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Music Preferences */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Music you love (optional)</label>
            <div className="flex flex-wrap gap-2">
              {MUSIC_GENRES.map(genre => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => toggleArrayItem('music_preferences', genre, 5)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.music_preferences.includes(genre) ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Ideology */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Political views (optional)</label>
            <div className="flex flex-wrap gap-2">
              {IDEOLOGIES.map(ideology => (
                <button
                  key={ideology}
                  type="button"
                  onClick={() => setFormData({...formData, ideology})}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.ideology === ideology ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  {ideology}
                </button>
              ))}
            </div>
          </div>

          {/* Green Flags */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Green flags you appreciate</label>
            <div className="flex flex-wrap gap-2">
              {GREEN_FLAGS.map(flag => (
                <button
                  key={flag}
                  type="button"
                  onClick={() => toggleArrayItem('green_flags', flag, 5)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.green_flags.includes(flag) ? 'bg-green-600 text-white' : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  {flag}
                </button>
              ))}
            </div>
          </div>

          {/* Red Flags */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Red flags you avoid</label>
            <div className="flex flex-wrap gap-2">
              {RED_FLAGS.map(flag => (
                <button
                  key={flag}
                  type="button"
                  onClick={() => toggleArrayItem('red_flags', flag, 5)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                    formData.red_flags.includes(flag) ? 'bg-red-600 text-white' : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  {flag}
                </button>
              ))}
            </div>
          </div>

          {/* Height */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Height</label>
            <select
              value={formData.height}
              onChange={(e) => setFormData({...formData, height: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            >
              <option value="">Prefer not to say</option>
              {HEIGHT_OPTIONS.map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>

          {/* Income Range */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Income Range</label>
            <select
              value={formData.income_range}
              onChange={(e) => setFormData({...formData, income_range: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
            >
              <option value="">Prefer not to say</option>
              {INCOME_RANGES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Gym/Fitness */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-2 block">Fitness/Gym Habits</label>
            <div className="grid grid-cols-2 gap-2">
              {GYM_OPTIONS.map(option => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setFormData({...formData, gym: option})}
                  className={`p-3 rounded-xl text-sm transition-all ${
                    formData.gym === option ? 'bg-primary text-white' : 'bg-secondary/50 text-white border border-white/10'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/* Hobbies */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Other hobbies (optional)</label>
            <textarea
              value={formData.hobbies}
              onChange={(e) => setFormData({...formData, hobbies: e.target.value.slice(0, 200)})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl p-4 text-white placeholder:text-gray-500 resize-none h-20"
              placeholder="What else do you enjoy doing?"
              maxLength={200}
            />
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save Changes
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
